<template>
	<div>
		<div v-if="!addOrEditReady">
			<LoadingSpinner class="col-12" />
		</div>
		<div v-else>
			<form ref="contractForm" @submit.prevent="saveInfo">
				<div class="row">

					<!-- COLONNE FORMULAIRE -->
					<div v-if="step === 1" class="col-12 col-lg-8">

						<!-- 1. CLIENT FACTURÉ -->
						<div class="mb-3">
							<div class="form-group mb-0">
								<label class="font-weight-bold">{{ $t('monte.client_facture') }} *</label>
								<div class="d-flex align-items-center">
									<e-select
										class="flex-grow-1 mr-2"
										v-model="form.tiers"
										track-by="tiers_id"
										label="tiers_rs"
										:placeholder="$t('monte.rechercher_tiers')"
										:selectedLabel="$t('global.selected_label')"
										:options="client_tiers"
										:allow-empty="false"
										:show-labels="false"
										:loading="loadingTiers"
										:class="{ 'is-invalid': errors.includes('tiers') }"
									/>
									<button type="button" class="btn btn-outline-primary flex-shrink-0" @click.prevent="$refs.tiersModal.openCreateModal()">
										<span class="d-none d-md-inline">{{ $t('tiers.ajouter_tiers') }} </span>+
									</button>
								</div>
							</div>
						</div>

						<!-- 2. ÉTALON -->
						<div class="mb-3">
							<Etalon
								ref="Etalon"
							:stallion_id="stallion_id"
							v-model="stallion"
							:contractconfig_id.sync="contractconfig_id"
							:season.sync="season"
							:mare="form.jument"
						/>
					</div>

						<!-- 3. JUMENT -->
						<div v-if="!has_no_conditions" class="mb-3">
							<div class="form-group mb-0">
								<label class="font-weight-bold">{{ $t('monte.jument') }} *</label>
								<div class="d-flex align-items-start">
									<e-select
										class="flex-grow-1 mr-2"
										v-model="form.jument"
										track-by="horse_id"
										label="horse_nom"
										:placeholder="$t('monte.rechercher_jument')"
										:selectedLabel="$t('global.selected_label')"
										:options="juments"
										:allow-empty="false"
										:show-labels="false"
										:loading="loadingJument"

										:sortable="false"
										:class="{ 'is-invalid': errors.includes('jument') }"
									/>
									<button type="button" class="btn btn-outline-primary flex-shrink-0" @click.prevent="add_mare">
										<span class="d-none d-md-inline">{{ $t('monte.creer_jument') }} </span>
										<font-awesome-icon :icon="['fal', 'plus']" />
									</button>
								</div>
							</div>
						</div>

						<!-- 4. LIEU DE MONTE -->
						<div v-if="!has_no_conditions" class="mb-3">
							<label class="font-weight-bold">{{ $t('monte.cmep') }}</label>
							<div class="d-flex align-items-center flex-wrap">
								<button
									v-if="form.cmep && form.cmep.tiers_id"
									type="button"
									class="btn btn-primary mr-2 mb-1"
								>
									<font-awesome-icon :icon="['fal', 'check']" class="mr-1" />
									{{ form.cmep.tiers_rs }}
								</button>
								<span v-if="form.cmep && form.cmep.tiers_id && !cmep_is_imposed" class="text-muted mr-2 mb-1">{{ $t('global.ou') }}</span>
								<button
									v-if="!cmep_is_imposed"
									type="button"
									class="btn btn-outline-secondary mb-1"
									@click.prevent="$refs.modalCmep.show()"
								>
									{{ $t('monte.select_autre_centre') }}
								</button>
							</div>
							<b-alert v-if="cmep_is_imposed" variant="warning" class="mt-2" show>
								<font-awesome-icon :icon="['fal', 'info-circle']" class="mr-1" />
								{{ $t('monte.cmep_imposed') }}
							</b-alert>
							<b-alert v-if="show_error_type_monte_cmep" variant="danger" class="mt-2" show>
								{{ $t('monte.error_type_monte_cmep') }}
							</b-alert>
						</div>

						<!-- 5. CONDITIONS DE MONTE -->
						<div v-if="contractconfig_id !== 0" class="mb-3">
							<TypeMonte
								ref="TypeMonte"
								:contractconfig_id="contractconfig_id"
								:tiers="form.tiers"
								v-model="type_monte"
								@no-conditions="has_no_conditions = $event"
							/>
						</div>
						<!-- Modèle (caché, auto-sélection) -->
						<Modele
							ref="Modele"
							v-show="false"
							v-model="modele"
							:contractconfig_id="contractconfig_id"
							auto-select-default-ca
						/>

						<!-- 6. CODE DE RÉDUCTION -->
						<div v-if="contract_allow_discount" class="mb-3">
							<div class="form-group mb-0">
									<label for="contract-code-reduction">{{ $t('monte.code_reduction') }}</label>
									<b-form-input id="contract-code-reduction" v-model="code_reduction" placeholder="XXXXX" />
							</div>
						</div>

						<!-- 7. COMMENTAIRE -->
						<div v-if="!has_no_conditions" class="mb-3">
							<div class="form-group mb-0">
									<label for="contract-commentaire">{{ $t('gynecologie.ajouter_commentaire') }}</label>
									<b-form-textarea id="contract-commentaire" v-model="commentaire" rows="5" />
							</div>
						</div>

						<!-- RÉCAP MOBILE (avant le bouton submit) -->
						<div v-if="!has_no_conditions" class="d-lg-none mb-3">
							<RecapSection
								:season="season"
								:stallion="stallion"
								:jument="form.jument"
								:cmep="form.cmep"
								:type_monte="type_monte"
								:tiers="form.tiers"
								:contract-saved="saved && step < 3"
								@preview="callPrintContracts"
							/>
						</div>

						<!-- CGV -->
						<AckTerms />
						<div v-if="form_message" class="col-12">
							<ErrorAlert :messageI18n="form_message" />
						</div>

						<!-- SUBMIT -->
						<div v-if="!has_no_conditions" class="form-group text-center mt-3">
							<button
								type="submit"
								class="btn btn-primary btn-block rounded-pill"
								:disabled="show_error_type_monte_cmep"
							@click.prevent="preview_mode = false; saveInfo()"
						>
							<font-awesome-icon v-if="processing && !preview_mode" :icon="['fas', 'spinner']" pulse />
							<template v-else>
								{{ $t('monte.creer_contrat') }}
								<font-awesome-icon :icon="['fal', 'arrow-right']" class="ml-1" />
							</template>
						</button>
						<button
							type="button"
							class="btn btn-outline-primary btn-block rounded-pill mt-2"
							@click.prevent="previewInfo()"
						>
							<font-awesome-icon v-if="processing && preview_mode" :icon="['fas', 'spinner']" pulse />
							<template v-else>
								<font-awesome-icon :icon="['fal', 'eye']" class="mr-1" />
								{{ $t('action.previsualiser') }}
								</template>
							</button>
						</div>
					</div>

					<!-- RÉCAP DESKTOP -->
					<div :class="step === 1 ? 'col-lg-4 d-none d-lg-block' : 'col-12 col-lg-4 mb-3 mb-lg-0'">
						<RecapSection
							:season="season"
							:stallion="stallion"
							:jument="form.jument"
							:cmep="form.cmep"
							:type_monte="type_monte"
							:tiers="form.tiers"
							:contract-saved="saved && step < 3"
							@preview="callPrintContracts"
						/>
					</div>

					<!-- ÉTAPE 2 : PAIEMENT (Yousign) ou SIGNATURE (canvas) -->
					<div v-if="step === 2" ref="step2" class="col-12 col-lg-8">
						<PaymentStep
							v-if="use_yousign"
							:amount="invoice_total"
							:paying="signing"
							:phase="yousign_phase"
							:signature-url="yousign_url"
							:invoice="yousign_invoice"
							:checking-signed="checkingSigned"
							:downloading-contract="downloadingSignedContract"
							:contract-id="contract ? contract.contract_id : null"
							@pay="onPayYousign"
							@check-signed="onCheckYousignSigned"
							@download-contract="onDownloadSignedContract"
							@payment-confirmed="onPaymentConfirmed"
							@signature-confirmed="yousign_phase = 'signed'"
						/>
						<SignatureStep
							v-else
								:amount="invoice_total"
								:signing="signing"
								@sign="onSign"
						/>
					</div>

					<!-- ÉTAPE 3 : CONFIRMATION -->
					<div v-if="step === 3" ref="step3" class="col-12 col-lg-8">
						<ConfirmationStep
							:contract-id="contract.contract_id"
						@new-contract="handleNewContract"
						/>
					</div>

				</div>
			</form>

			<!-- MODAL SÉLECTION CMEP -->
			<b-modal ref="modalCmep" :title="$t('monte.select_cmep_title')" hide-footer size="lg">
				<ChangementCMEP
					:contract_id="null"
					:tiers_tab="cmep_tiers"
					:cmep="null"
					:required="false"
					:type_monte="type_monte_code"
					:default_country="form.tiers ? form.tiers.tiers_country : 75"
					:default_postal="form.tiers ? form.tiers.tiers_postalcode : null"
					:fixed_tiers="eligible_cmep_tiers"
					@update:cmep="onCmepSelectedFromModal"
				/>
			</b-modal>

			<!-- MODAL PRÉVISUALISATION PDF -->
			<b-modal size="xl" ref="modalPreview" hide-footer @hidden="preview_base64 = null">
				<template v-slot:modal-title>
					{{ $t('action.previsualiser') }}
				</template>
				<iframe v-if="preview_base64 != null" :src="'data:application/pdf;base64,' + preview_base64" height="1000px" width="100%" class="contract-preview-iframe"></iframe>
				<div v-else>
					<LoadingSpinner />
				</div>
			</b-modal>

			<!-- MODAL COMPLÉTUDE TIERS -->
			<TiersModal ref="tiersModal" :allowed_countries="allowed_countries" @submit="onTiersChecked" @cancel="onTiersCancel" @contact-us="goToContactUs" @country-blocked="onTiersCountryBlocked" />

			<!-- MODAL PAYS NON AUTORISÉ -->
			<b-modal ref="modalCountryBlocked" :title="$t('monte.country_not_allowed_title')" hide-footer centered no-close-on-backdrop @hidden="form.tiers = null">
				<div class="d-flex align-items-start mb-4">
					<font-awesome-icon :icon="['fal', 'globe']" class="mr-3 mt-1" style="font-size:1.5rem; color: var(--primary);" />
					<p class="mb-0">{{ $t('monte.country_not_allowed_message') }}</p>
				</div>
				<div class="d-flex flex-column">
					<button type="button" class="btn btn-primary mb-2" @click.prevent="goToContactUs">
						<font-awesome-icon :icon="['fal', 'envelope']" class="mr-1" />
						{{ $t('monte.contact_us') }}
					</button>
					<button type="button" class="btn btn-outline-secondary" @click.prevent="resetBlockedTiers">
						<font-awesome-icon :icon="['fal', 'user-times']" class="mr-1" />
						{{ $t('monte.choose_other_client') }}
					</button>
				</div>
			</b-modal>

			<!-- MODAL PAIEMENT EXISTANT (factures adressées) -->
			<b-modal ref="modal" hide-footer>
				<template v-slot:modal-title>
					{{ $t("action.online_paiement") }}
				</template>
				<PaymentForm
					ref="PaymentForm"
					:invoicedetails_ids="invoicedetails_ids"
					:details="details"
				/>
			</b-modal>

			<!-- MODAL ERREURS PRÉVISUALISATION -->
			<b-modal ref="modalPreviewErrors" :title="$t('monte.preview_champs_manquants_titre')" hide-footer centered>
				<div class="d-flex align-items-start">
					<font-awesome-icon :icon="['fal', 'exclamation-triangle']" class="text-warning mr-3 mt-1 contract-warning-icon" />
					<div>
						<p class="mb-1">{{ $t('monte.preview_champs_manquants_desc') }}</p>
						<p v-if="form_message" class="mb-0 font-weight-bold">{{ $t(form_message) }}</p>
					</div>
				</div>
			</b-modal>

			<!-- MODAL CHOIX DU MODE DE PAIEMENT -->
			<b-modal ref="modalPaiement" :title="$t('monte.modal_paiement_titre')" hide-footer centered>
				<p class="text-muted small mb-3">{{ $t('monte.modal_paiement_desc') }}</p>
				<div class="d-flex flex-column">
					<button
						v-if="marketplace_type"
						type="button"
						class="btn btn-primary mb-2"
						:disabled="signing"
						@click.prevent="handlePayOnline"
					>
						<b-spinner v-if="signing" small class="mr-1" />
						<font-awesome-icon v-else :icon="['fal', 'credit-card']" class="mr-1" />
						{{ $t('monte.payer_en_ligne') }}
					</button>
					<button
						v-if="allow_payment_later"
						type="button"
						class="btn btn-outline-primary"
						:disabled="signing"
						@click.prevent="handlePayLater"
					>
						<b-spinner v-if="signing" small class="mr-1" />
						<font-awesome-icon v-else :icon="['fal', 'clock']" class="mr-1" />
						{{ $t('monte.payer_plus_tard') }}
					</button>
				</div>
			</b-modal>

			<!-- Formulaire caché pour redirection vers Paybox -->
			<form v-if="go_paybox" method="POST" :action="paybox_infos.url" id="redirectFormPaybox" class="d-none">
				<template v-for="(value, key) in paybox_infos">
					<input v-if="key !== 'url'" :key="key" type="hidden" :name="key" :value="value">
				</template>
				<button id="button_go_paybox_contract" type="submit">{{ $t('global.valider') }}</button>
			</form>

			<!-- Formulaire caché Sogecommerce -->
			<form v-if="go_sogecommerce" method="POST" :action="sogecommerce_infos.url" id="redirectFormSogecommerce" class="d-none">
				<template v-for="(value, key) in sogecommerce_infos">
					<input v-if="key !== 'url'" type="hidden" :name="key" :value="value" :key="key">
				</template>
				<button id="button_go_sogecommerce_contract" type="submit">{{ $t('global.valider') }}</button>
			</form>
		</div>
	</div>
</template>

<script type="text/javascript">
    import { EventBus } from 'EventBus'
	import Navigation from "@/mixins/Navigation.js"
	import ContractMixin from "@/mixins/Contract.js"
	import ModelMixin from "@/mixins/Model.js"
	import TiersMixin from "@/mixins/Tiers.js"
	import HorseMixin from "@/mixins/Horse.js"
    import Marketplace from '@/mixins/Marketplace.js'
    import Invoice from '@/mixins/Invoice.js'

	import ShutterContract from "@/mixins/shutters-manager/Contract.js"
	import Shutter from '@/mixins/Shutter.js'
	import _sortBy from 'lodash/sortBy'

	export default {
		name: "ContractForm",
		props: {
			from: {
				type: String,
				default: () => ( "" )
			},
			stallion_id: {
				type: Number,
				default: () => ( 0 )
			},
            mare_id: {
				type: Number,
				default: () => ( 0 )
			}
		},
		mixins: [Navigation, ContractMixin, ModelMixin, TiersMixin, Shutter, ShutterContract, HorseMixin, Marketplace, Invoice],
		data () {
			return {
				form_message: "",
				addOrEditReady: false,
				disabledCmep: false,
				loadingTiers: false,
				loadingStallion: false,
				loadingModele: false,
				loadingTypeMonte: false,
				require_infos: true,
				loadingJument: false,
				conditions_spe: false,
				table_busy: false,
				contractconfig_id: 0,
				has_no_conditions: false,
				processing: false,
				commentaire: '',
				errors: [],
				conditions: [],
				juments: [],
				articles: [],
				tiers: [],
				formattedAccount: [],
				cmep_tiers: [],
				entities: [],
				contract_config: {}, //Contract config selectionné en fonction de l'étalon et de la saison
				contract: {}, //Contract créé après submit

				form: {
					tiers:  {},
					season:  {},
					stallion:  {},
					modele:  null,
					type_monte:  null,
					jument: null,
					cmep: null,
					courtier: null,
				},
				labelTitleTiers: this.getTrad("monte.rechercher_tiers"),
				selectedLabelTiers: this.getTrad("global.selected_label"),
				labelTitleJument: this.getTrad("monte.rechercher_jument"),
				selectedLabelJument: this.getTrad("global.selected_label"),
				labelTitleCmep: this.getTrad("monte.rechercher_cmep"),
				selectedLabelCmep: this.getTrad("global.selected_label"),
				labelTitleCourtier: this.getTrad("monte.rechercher_courtier"),
				selectedLabelCourtier: this.getTrad("global.selected_label"),
				extern_slot_columns: [
				    'avenantarticles_ht',
				    'tiers.tiers_rs',
				    'author.tiers_rs',
				    'avenantarticles_invoiceable_date',
				],

				/* NEW */
				season: {},
				stallion: {},
				modele: {},
				type_monte: {},
				avenant_article: {},
				season_mare: null,
				season_id: null,
                saved: false,
                has_invoice: false,
                ask_manual_cmep: false,
				invoice_mode: "after",


				modal_loaded: false,
				items_to_pay: null,
                url_boutique: null,
                marketplace: 'mangopay',
				accounts: [],
                dates: {},
                go_atos: false,
                go_paybox: false,
                go_sogecommerce: false,
                atos_infos: {},
                paybox_infos: {},
                sogecommerce_infos: {},
                invoicedetails_ids: [],
                details: [],
                compte: null,
				current_date: new Date(),
				total_balance: 0,
				type_monte_code: '',
				nombre_paillette: 0,
				show_error_type_monte_cmep: false,
				show_create_cmep: false,
				client_tiers: [],
				tiers_updating: false,
				code_reduction: '',
				contract_allow_discount: true,
				use_electronic_signature: false,
				allow_payment_later: true,
				allowed_countries: [],
				preview_mode: false,
				preview_base64: null,
				step: 1,
				signing: false,
				signature_pending: null,
				marketplace_type: null,
				yousign_phase: 'payment',
				yousign_url: null,
				yousign_invoice: null,
				checkingSigned: false,
				downloadingSignedContract: false,
			}
		},
		created() {
			this.init_component()
		},
		methods: {
			/**
			 * Initialise la configuration du formulaire : mode de facturation et politique de remise.
			 * Appelée en premier lors de l'initialisation du composant.
			 */
			async initConfig() {
				this.show_create_cmep = this.getConfig("create_cmep") ?? false
				const [invoiceMode, privacy, mp] = await Promise.all([
					this.contractInvoiceMode(),
					this.contractPrivacyPolicy(),
					this.getMarketplaceType(),
				])
				this.invoice_mode = invoiceMode.contract_invoice_mode
				const mpType = mp && mp.retour && mp.retour.type
				this.marketplace_type = (mpType && mpType !== 'none') ? mpType : null
				if(privacy) {
					this.contract_allow_discount = privacy.contract_allow_discount ?? true
					this.use_electronic_signature = privacy.use_electronic_signature ?? false
					this.allow_payment_later = privacy.allow_payment_later ?? true
					this.allowed_countries = Array.isArray(privacy.allowed_countries) ? privacy.allowed_countries : []
				}
			},
			async initTiers() {
				this.loadingTiers = true
				this.client_tiers = this.getConfig('all_tiers') || []
				this.cmep_tiers = await this.loadCmepTiersWithCountry()
				this.loadingTiers = false

				if(this.contract.tiers != undefined) {
					this.form.tiers = this.contract.tiers
				}

				//Je vais restreindre mes tiers si je viens de la fiche jument
				if(this.from == "mareList") {
					this.juments = this.juments.filter(el => el.horse_id == this.stallion_id)
				}
			},
			async initJuments() {
				this.loadingJument = true
				let juments = await this.loadJuments()

				let contract_sire = this.getConfig('contract_sire_obligatory') || false
				if(contract_sire) {
					juments = juments.filter(jument => jument.horse_sire !== null && jument.horse_sire !== '')
				}

				this.juments = _sortBy(juments, ['horse_nom'])

                if(this.mare_id !== 0) {
                    this.form.jument = this.juments.find(j => j.horse_id == this.mare_id)
                }

				this.loadingJument = false
			},
			/**
			 * Scroll vers le haut du formulaire ou d'une étape spécifique.
			 * Prend en compte la hauteur du header fixe.
			 * @param {string} refName - Nom de la ref vers laquelle scroller (optionnel)
			 */
			scrollToFormTop(refName = 'contractForm') {
				// Double nextTick + setTimeout pour laisser le temps au DOM de se mettre à jour
				this.$nextTick(() => {
					setTimeout(() => {
						const header = document.querySelector('#mobile_header') || document.querySelector('#nav_top_bar') || document.querySelector('nav')
						const headerHeight = header?.offsetHeight || 80
						const target = this.$refs[refName]
						if (target) {
							const targetTop = target.getBoundingClientRect().top + window.scrollY
							window.scrollTo({ top: targetTop - headerHeight - 10, behavior: 'smooth' })
						} else {
							window.scrollTo({ top: 0, behavior: 'smooth' })
						}
					}, 100)
				})
			},
			async initForm() {
				// Aucune action en mode création
			},
			/**
			 * Réinitialise l’état du formulaire pour démarrer la création d’un nouveau contrat.
			 */
			handleNewContract() {
				this.step = 1
				this.scrollToFormTop()
				this.contract = {}
				this.errors = []
				this.form_message = ''
				this.saved = false
				this.has_invoice = false
				this.signing = false
				this.signature_pending = null
				this.preview_mode = false
				this.preview_base64 = null
				this.yousign_phase = 'payment'
				this.yousign_url = null
				this.yousign_invoice = null
				this.form = {
					tiers: {},
					season: {},
					stallion: {},
					modele: null,
					type_monte: null,
					jument: null,
					cmep: null,
					courtier: null,
				}
				this.code_reduction = ''
				this.commentaire = ''
			},
			async init_component() {
				this.addOrEditReady = false

				await this.initConfig()

				await Promise.all([
					this.initTiers(),
					this.initJuments(),
				])

				const seasons = await this.loadSeasons()
				const date = new Date()
				this.season = seasons.find(season => Date.parseTz(season.season_start) <= Date.parseTz(date) && Date.parseTz(season.season_end) >= Date.parseTz(date))
				this.season_id = this.season.season_id

				this.addOrEditReady = true
			},
			async saveInfo() {
				if(this.processing == false) {
					this.processing = true

					this.errors = []
					this.form_message = ''

					if(!this.form.tiers || !this.form.tiers.tiers_id) {
						this.errors.push('tiers')
						this.form_message = 'monte.tiers_not_set'
					}

					if(this.contractconfig_id == 0) {
						this.errors.push("season")
						this.errors.push("stallion")
						this.form_message = "monte.contract_config_not_found"
					}
					else if (!this.type_monte) {
						this.errors.push('type_monte')
						this.form_message = 'monte.type_monte_not_set'
					}

					if(!this.form.cmep && !this.show_create_cmep) {
						this.errors.push('cmep')
						this.form_message = 'monte.type_cmep_not_set'
					}

					if(!this.form.jument) {
						this.errors.push('jument')
						this.form_message = 'monte.type_jument_not_set'
					}

					if (this.form_message) {
						this.processing = false
						return
					}

					// 1. Réutiliser le contrat brouillon existant (preview précédente) ou en créer un nouveau
					let newContract = this.contract && this.contract.contract_id ? this.contract : null
					if (!newContract) {
						newContract = await this.addContract(this.form.tiers.tiers_id)
						if (!newContract || !newContract.contract_id) {
							this.form_message = 'monte.contract_creation_error'
							this.processing = false
							return
						}
						this.contract = newContract
					}

					// 2. Sauvegarder les données du formulaire dans le contrat
					const infos = {
						cmep: this.form.cmep ? this.form.cmep.tiers_id : null,
						type_monte: this.type_monte.id,
						modele: this.modele.model_id,
						jument: this.form.jument.horse_id,
						contractconfig_id: this.contractconfig_id,
						avenant: newContract.last_avenant.avenant_id,
						change_avenant: true,
						season: this.season.season_id,
						paillettes: this.show_paillettes ? this.nombre_paillette : null,
						note: this.commentaire || ''
					}

					await this.saveContract(newContract.contract_id, infos)
					const invoice = (this.preview_mode || this.marketplace_type) ? null : await this.contractAutoInvoice(newContract.contract_id, this.invoice_mode)

				if(this.preview_mode) {
					this.$refs.modalPreview.show()
					const base64 = await this.printContract(newContract.contract_id, true)
					this.preview_base64 = base64
					this.saved = true
					this.processing = false
				}
				else {
					if(invoice && invoice.invoicedetails_id != undefined) {
						this.invoicedetails_ids = invoice.invoicedetails_id
						this.details = invoice.tab_invoice
					}
					this.has_invoice = Boolean(invoice)
					this.saved = true
					this.processing = false
					this.step = 2
					this.scrollToFormTop('step2')
				}
			}
		},
			previewInfo() {
				this.errors = []
				this.form_message = ''

				if(!this.form.tiers || !this.form.tiers.tiers_id) {
					this.errors.push('tiers')
					this.form_message = 'monte.tiers_not_set'
				}
				if(this.contractconfig_id == 0) {
					this.errors.push('season')
					this.errors.push('stallion')
					if (!this.form_message) this.form_message = 'monte.contract_config_not_found'
				} else if (!this.type_monte) {
					this.errors.push('type_monte')
					if (!this.form_message) this.form_message = 'monte.type_monte_not_set'
				}
				if(!this.form.cmep && !this.show_create_cmep) {
					this.errors.push('cmep')
					if (!this.form_message) this.form_message = 'monte.type_cmep_not_set'
				}
				if(!this.form.jument) {
					this.errors.push('jument')
					if (!this.form_message) this.form_message = 'monte.type_jument_not_set'
				}

				if (this.form_message) {
					this.$refs.modalPreviewErrors.show()
					return
				}

				this.preview_mode = true
				this.saveInfo()
			},
			async callPrintContracts() {
				if(!this.contract || !this.contract.contract_id) return
				const result = await this.printContract(parseInt(this.contract.contract_id))
				if(result == null) {
					this.failureToast('monte.print_error')					
				}
			},
            redirectPostSave() {
                if(this.has_invoice) {
                    this.$router.push({ name: 'addressedInvoices' })
                }
                else {
                    this.shutterPanel().close('contract-form')
                    EventBus.$emit('ContractList::backToList')
                }
            },
			add_mare() {
				// stallion peut être l'objet complet (sélecteur) ou juste l'id (prop directe)
				const sid = (this.stallion && this.stallion.horse_id) ? this.stallion.horse_id : this.stallion_id
				// from=contract : après création de la jument, HorseAjoutBeta/HorseCreation redirige vers ContractNew
				this.$router.push({ name: 'horseAjout', query: { from: 'contract', stallion_id: sid }})
			},

			/**
			 * Affiche la modale "pays non autorisé" suite à une tentative de création depuis TiersModal.
			 */
			onTiersCountryBlocked() {
				this.$refs.modalCountryBlocked.show()
			},
			/**
			 * Callback déclenché lorsque l'utilisateur ferme volontairement la modale
			 * InformationsTiersForm (croix ou touche Échap). Réinitialise simplement
			 * la sélection du tiers sans afficher de message d'erreur.
			 */
			onTiersCancel() {
				this.form.tiers = null
			},
			/**
			 * Ferme la modale de blocage pays et réinitialise le tiers sélectionné.
			 */
			resetBlockedTiers() {
				this.$refs.modalCountryBlocked.hide()
				this.form.tiers = null
			},
			/**
			 * Ferme la modale et redirige vers la page de contact.
			 */
			goToContactUs() {
				this.$refs.modalCountryBlocked.hide()
				this.$router.push({ name: 'contactUs' })
			},
			/**
			 * Callback du popup InformationsTiersForm après vérification/complétion des infos.
			 * @param {boolean|Object} result - false si échec, true si succès sans changement de tiers,
			 *                                  ou un objet tiers si le tiers a été mis à jour ou créé.
			 */
			async onTiersChecked(result) {
				if (!result) {
					this.form.tiers = null
					this.form_message = 'monte.erreur_infos_tiers'
				} else if (typeof result === 'object' && result.tiers_id) {
					const isSameTiers = this.form.tiers && Number(result.tiers_id) === Number(this.form.tiers.tiers_id)
					if (isSameTiers) {
						// Fusionner l'objet courant avec les données fraîches du formulaire
						const updated = { ...this.form.tiers, ...result }
						// Mettre à jour l'entrée dans client_tiers pour garder les options cohérentes
						const idx = this.client_tiers.findIndex(t => Number(t.tiers_id) === Number(result.tiers_id))
						if (idx !== -1) this.$set(this.client_tiers, idx, updated)
						// Trick null→valeur pour forcer le re-render complet de vue-multiselect
						this.tiers_updating = true
						this.form.tiers = null
						await this.$nextTick()
						this.form.tiers = updated
						await this.$nextTick()
						this.tiers_updating = false
					} else {
						// Nouveau tiers créé (conversion physique → morale OU création directe)
						// S'il n'est pas encore dans la liste des options, l'y ajouter
						const existsInList = this.client_tiers.some(t => Number(t.tiers_id) === Number(result.tiers_id))
						if (!existsInList) {
							this.client_tiers.push(result)
						}
						this.form.tiers = result
					}
				}
			},

			/**
			 * Callback déclenché lors de la sélection d'un CMEP depuis la modal.
			 * @param {Object} cmep - L'objet tiers CMEP sélectionné.
			 */
			onCmepSelectedFromModal(cmep) {
				if (cmep && cmep.tiers_id) {
					this.form.cmep = cmep
					this.$refs.modalCmep.hide()
				}
			},
			/**
			 * Reçoit la signature depuis SignatureStep et ouvre la modale de choix du mode de paiement.
			 * @param {string} signatureData - Image base64 de la signature (data URL PNG).
			 */
			async onSign(signatureData) {
				if (!signatureData || !this.contract || !this.contract.contract_id) return
				this.signature_pending = signatureData
				const mp = await this.getMarketplaceType()
				const mpType = mp && mp.retour && mp.retour.type
				this.marketplace_type = (mpType && mpType !== 'none') ? mpType : null
				this.$refs.modalPaiement.show()
			},

			/**
			 * Payer plus tard — signe le contrat et affiche la confirmation.
			 */
			async onPayLater() {
				this.$refs.modalPaiement.hide()
				this.signing = true
				try {
					await this.signContractWithCanvas(this.contract.contract_id, this.signature_pending)
					this.step = 3
					this.scrollToFormTop('step3')
				} catch(e) {
					console.error('ContractForm::onPayLater => ERROR', e)
					this.failureToast('monte.erreur_signature')
				} finally {
					this.signing = false
				}
			},

			/**
			 * Soumet le formulaire caché de redirection vers la plateforme de paiement.
			 * @param {Object} data — données retournées par l'API boutique (url + champs)
			 * @param {String} suffix — suffixe du bouton de soumission (ex: 'contract')
			 */
			submitMarketplaceForm(data, suffix) {
				if (this.marketplace_type === 'paybox') {
					this.paybox_infos = data
					this.go_paybox = true
					this.$nextTick(() => {
						document.querySelector("#button_go_paybox_" + suffix).click()
					})
				} else if (this.marketplace_type === 'sogecommerce') {
					this.sogecommerce_infos = data
					this.go_sogecommerce = true
					this.$nextTick(() => {
						document.querySelector("#button_go_sogecommerce_" + suffix).click()
					})
				} else {
					window.location.href = data.url
				}
			},

			/**
			 * Payer en ligne — envoie la signature + contract_id, obtient l'URL boutique
			 * et redirige l'utilisateur vers la page de paiement.
			 * Pour Paybox/Sogecommerce : soumission d'un formulaire POST caché.
			 * Pour CAWL/PayPlug : redirection directe via URL.
			 */
			async onPayOnline() {
				this.$refs.modalPaiement.hide()
				this.signing = true
				try {
					const boutique = await this.getBoutiqueContractUrl(this.contract.contract_id, this.signature_pending)
					if (boutique && boutique.url) {
						this.submitMarketplaceForm(boutique, 'contract')
					}
				} catch(e) {
					console.error('ContractForm::onPayOnline => ERROR', e)
					this.failureToast('monte.erreur_signature')
				} finally {
					this.signing = false
				}
			},

			/** Délègue le choix de paiement selon le mode (canvas ou Yousign). */
			handlePayLater() {
				if (this.use_yousign) this.onPayLaterYousign()
				else this.onPayLater()
			},
			/** Délègue le paiement en ligne selon le mode (canvas ou Yousign). */
			handlePayOnline() {
				if (this.use_yousign) this.onPayOnlineYousign()
				else this.onPayOnline()
			},

			/** Ouvre la modal de choix de paiement (mode Yousign), ou bascule directement si un seul choix. */
			async onPayYousign() {
				const mp = await this.getMarketplaceType()
				const mpType = mp && mp.retour && mp.retour.type
				this.marketplace_type = (mpType && mpType !== 'none') ? mpType : null

				const hasOnline = !!this.marketplace_type
				const hasLater  = !!this.allow_payment_later

				if (hasOnline && !hasLater) return this.onPayOnlineYousign()
				if (!hasOnline && hasLater)  return this.onPayLaterYousign()
				this.$refs.modalPaiement.show()
			},

			/** Payer plus tard (Yousign) — finalise la facture puis génère le lien Yousign. */
			async onPayLaterYousign() {
				this.$refs.modalPaiement.hide()
				this.signing = true
				try {
					await this.finalizeContract(this.contract.contract_id)
					const url = await this.getContractSignatureLink(this.contract.contract_id)
					this.yousign_url = url
					const invoices = await this.loadInvoiceContract(this.contract.contract_id)
					this.yousign_invoice = invoices && invoices.length > 0 ? invoices[0] : null
					this.yousign_phase = 'signing'
				} catch(e) {
					console.error('ContractForm::onPayLaterYousign => ERROR', e)
					this.failureToast('monte.erreur_signature')
				} finally {
					this.signing = false
				}
			},

			/** Payer en ligne (Yousign) — ouvre la boutique paiement dans le même onglet (retour banque sur payment-result). */
			async onPayOnlineYousign() {
				this.$refs.modalPaiement.hide()
				this.signing = true
				try {
					const boutique = await this.getBoutiqueContractUrl(this.contract.contract_id)
					if (boutique && boutique.url) {
						this.submitMarketplaceForm(boutique, 'contract')
					}
					// Passer en phase 'awaiting_payment' — PaymentStep va lancer le polling
					this.yousign_phase = 'awaiting_payment'
				} catch(e) {
					console.error('ContractForm::onPayOnlineYousign => ERROR', e)
					this.failureToast('monte.erreur_signature')
				} finally {
					this.signing = false
				}
			},

			/**
			 * Appelé par PaymentStep quand le polling détecte le paiement capturé.
			 * Finalise le contrat et génère le lien Yousign.
			 */
			async onPaymentConfirmed() {
				this.signing = true
				try {
					await this.finalizeContract(this.contract.contract_id)
					const url = await this.getContractSignatureLink(this.contract.contract_id)
					this.yousign_url = url
					const invoices = await this.loadInvoiceContract(this.contract.contract_id)
					this.yousign_invoice = invoices && invoices.length > 0 ? invoices[0] : null
					this.yousign_phase = 'signing'
				} catch(e) {
					console.error('ContractForm::onPaymentConfirmed => ERROR', e)
					this.failureToast('monte.erreur_signature')
				} finally {
					this.signing = false
				}
			},

			/** Vérifie si la signature Yousign est complète — passe en phase 'signed' si oui. */
			async onCheckYousignSigned() {
				this.checkingSigned = true
				try {
					const status = await this.getContractSignatureStatus(this.contract.contract_id)
					if (status && status.signed) {
						this.yousign_phase = 'signed'
					} else {
						this.failureToast('monte.signature_yousign_en_attente')
					}
				} catch(e) {
					console.error('ContractForm::onCheckYousignSigned => ERROR', e)
				} finally {
					this.checkingSigned = false
				}
			},

			/** Télécharge le contrat signé (phase signed). */
			async onDownloadSignedContract() {
				this.downloadingSignedContract = true
				try {
					await this.printContract(this.contract.contract_id)
				} catch(e) {
					console.error('ContractForm::onDownloadSignedContract => ERROR', e)
					this.failureToast('monte.print_error')
				} finally {
					this.downloadingSignedContract = false
				}
			},
		},
		computed: {
			/**
			 * Calcule le montant total de la facture en euros.
			 * Utilise les montants HT si le code TVA est exonéré (FDVXP/FDVEX),
			 * sinon les montants TTC. Retourne le prix catalogue si aucune facture n'est encore créée.
			 * @returns {number} Montant en euros (déjà divisé par 100).
			 */
			invoice_total() {
				const vat_code = this.type_monte?.articles?.[0]?.vat_code
				const isExempt = ['FDVXP', 'FDVEX'].includes(vat_code)
				if(this.details && this.details.length > 0) {
					return this.details.reduce((sum, d) => {
						const inv = d.invoice
						if(!inv) return sum
						return sum + (isExempt ? (inv.invoice_ht || 0) : (inv.invoice_balance || 0))
					}, 0) / 100
				}
				if(this.type_monte?.articles?.length > 0) {
					return this.type_monte.articles
						.filter(a => a.ca_invoice_enabled !== false)
						.reduce((sum, a) => sum + (a.articles_ttc || 0), 0)
				}
				return 0
			},
			show_paillettes() {
				return ['iac', 'iarr', 'icsi', 'vp'].includes(this.type_monte_code)
			},
			/** Liste des centres éligibles selon la condition sélectionnée. null = pas de restriction. */
			eligible_cmep_tiers() {
				if(this.type_monte && this.type_monte.cmeps && this.type_monte.cmeps.length > 0) {
					return this.type_monte.cmeps
				}
				return null
			},
			/** Le lieu de monte est imposé si en frais (disabledCmep) ou si la condition n'a qu'un seul centre autorisé. */
			cmep_is_imposed() {
				if (this.disabledCmep) return true
				if (this.type_monte && this.type_monte.cmeps && this.type_monte.cmeps.length === 1) return true
				return false
			},
			/** Active le flow Yousign si la licence a activé la signature électronique. */
			use_yousign() {
				return !!this.use_electronic_signature
			},
		},
		watch: {
			async type_monte(val) {
				if(val && val.type_monte) {
					this.type_monte_code = val.type_monte
					this.nombre_paillette = val.paillettes
				}
				if(val && ['mm', 'iaf', 'mn'].includes(val.type_monte)) {
					let default_cmep = await this.getContractConfigDefaultCmep(val.contractconfig)
					if(default_cmep && default_cmep.tiers_id) {
						this.form.cmep = default_cmep
						this.disabledCmep = true
					} else {
						this.disabledCmep = false
					}
				}
				else {
					this.disabledCmep = false
				}

				this.show_error_type_monte_cmep = false
				if(val && val.cmeps.length > 0) {
					// si pas de cmep sélectioné et un seul val.cmeps le sélectionner
					if((!this.form.cmep || (this.form.cmep && !this.form.cmep.tiers_id)) && val.cmeps.length == 1) {
						this.form.cmep = val.cmeps[0]
					}
					// si cmep sélectionné mais pas dans val.cmeps afficher erreurs
					if(this.form.cmep && this.form.cmep.tiers_id) {
						this.show_error_type_monte_cmep = !val.cmeps.find(cmep => cmep.tiers_id == this.form.cmep.tiers_id)
					}
				}
			},

			'form.cmep' (val) {
				this.show_error_type_monte_cmep = false
				if(val && val.tiers_id && this.type_monte && this.type_monte.cmeps && this.type_monte.cmeps.length > 0) {
					this.show_error_type_monte_cmep = !this.type_monte.cmeps.find(cmep => cmep.tiers_id == val.tiers_id)
				}
			},

			async 'form.tiers'(newVal) {
				// Seulement après l'initialisation complète (changement manuel par l'utilisateur)
				if(!this.addOrEditReady) return
				if(this.tiers_updating) return
				if(this.disabledCmep) return
				this.form.cmep = null
				if(newVal && newVal.tiers_id) {
					// Vérifier si le pays du tiers est autorisé
					if(this.allowed_countries.length > 0) {
						const countryId = newVal.tiers_country
						if(!countryId || !this.allowed_countries.includes(parseInt(countryId))) {
							this.$refs.modalCountryBlocked.show()
							return
						}
					}
					const most_used_id = await this.getMostUsedCmep(newVal.tiers_id)
					if(most_used_id) {
						this.form.cmep = this.cmep_tiers.find(t => t.tiers_id == most_used_id) || null
					}

					// Vérifier la complétude des infos du tiers sélectionné
					this.$refs.tiersModal.openModal(newVal)
				}
			}
		},
		components: {
			LoadingSpinner : () => import('GroomyRoot/components/Logos/LoadingSpinner_35'),
			CustomTable : () => import('GroomyRoot/components/Table/CustomTable'),
			ErrorAlert : () => import('GroomyRoot/components/Alert/ErrorAlert'),
			ChangementCMEP : () => import('@/components/Contract/AvenantType/ChangementCMEP'),
			Saison : () => import('@/components/Contract/Type/Saison'),
			Etalon : () => import('@/components/Contract/Type/Etalon'),
			Modele : () => import('@/components/Contract/Type/Modele'),
			TypeMonte : () => import('@/components/Contract/Type/TypeMonte'),
			AckTerms : () => import('@/components/Contract/PrivacyPolicyAck'),
			RequiredInfosUser : () => import('GroomyRoot/components/Marketplace/RequiredInfosUser'),
			PaymentForm : () => import('@/components/Payment/Form'),
			RecapSection : () => import('@/components/Contract/RecapSection'),
			SignatureStep : () => import('@/components/Contract/SignatureStep'),
			PaymentStep : () => import('@/components/Contract/PaymentStep'),
			ConfirmationStep : () => import('@/components/Contract/ConfirmationStep'),
			TiersModal : () => import('@/components/Contract/InformationsTiersForm'),
		}
	}
</script>
